import {Dayjs} from "dayjs";
import {
    FrequentationActs,
    FrequentationDay,
    FrequentationHour,
    FrequentationWeek,
    FrequentationYearWeek
} from "@/core/interfaces/Frequentation";

/**
 * The four attendance levels, quiet to peak: one hue, four intensities.
 *
 * The brand violet (`primary`, #3513e2) laid over white at 10, 26, 44 and
 * 68 % — the charter's own way of declining a brand colour, and the hue the
 * mockup gives the activity curve. Four different hues read as four
 * unrelated states; one ramp reads as a scale, which is what a load is.
 * `text` is the ink legible over each step (contrast 10.1, 7.3, 4.9 and
 * 4.6:1), for the displays that write inside a cell. This list is the only
 * place to touch to retune the scale.
 */
export const FREQUENTATION_LEVELS = [
    {level: 1, label: 'calme', color: '#ebe7fc', text: '#353535'},
    {level: 2, label: 'modéré', color: '#cac2f7', text: '#353535'},
    {level: 3, label: 'soutenu', color: '#a697f2', text: '#353535'},
    {level: 4, label: 'forte affluence', color: '#765feb', text: '#ffffff'}
] as const;

const levelEntry = (level: number) => {
    return FREQUENTATION_LEVELS.find((entry) => entry.level === level) ?? FREQUENTATION_LEVELS[0];
};

export const colorForLevel = (level: number): string => levelEntry(level).color;

/** Ink legible over `colorForLevel(level)`. */
export const textForLevel = (level: number): string => levelEntry(level).text;

export const labelForLevel = (level: number): string => levelEntry(level).label;

/**
 * The expected load of the given day, null when the week holds nothing for
 * it — the pharmacy is closed that day.
 */
export const frequentationDayOf = (week: FrequentationWeek | null, day: Dayjs): FrequentationDay | null => {
    const date = day.format('YYYY-MM-DD');

    return week?.days.find((entry) => entry.date === date) ?? null;
};

/**
 * The slot expected for the given day and hour, null when the history holds
 * nothing for it — the overlay then simply leaves that hour blank.
 */
export const frequentationSlot = (week: FrequentationWeek | null, day: Dayjs, hour: number): FrequentationHour | null => {
    return frequentationDayOf(week, day)?.hours.find((slot) => slot.hour === hour) ?? null;
};

const integer = new Intl.NumberFormat('fr-FR', {maximumFractionDigits: 0});

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const plural = (count: number, singular: string, pluralForm: string) => `${integer.format(count)} ${count >= 2 ? pluralForm : singular}`;

/**
 * The long acts of a slot or a week, in words: vaccinations (flu and others
 * together, the patient does not care which code) and rapid tests. Empty
 * when none weighed in.
 */
export const describeActs = (acts: FrequentationActs): string => {
    const vaccinations = Math.round((acts.VGP ?? 0) + (acts.RVA ?? 0));
    const tests = Math.round(acts.TRD ?? 0);
    const parts: string[] = [];

    if (vaccinations > 0) {
        parts.push(plural(vaccinations, 'vaccination', 'vaccinations'));
    }

    if (tests > 0) {
        parts.push(plural(tests, 'test rapide', 'tests rapides'));
    }

    return parts.join(', ');
};

/**
 * Signed percentage of a value against a baseline: « +18 % », « −7 % ».
 */
const deviation = (value: number, baseline: number): string | null => {
    if (baseline <= 0) {
        return null;
    }

    const percent = Math.round(((value - baseline) / baseline) * 100);

    return `${percent > 0 ? '+' : percent < 0 ? '−' : ''}${Math.abs(percent)} %`;
};

/**
 * What justifies the colour of a slot, for the tooltip: the level in words,
 * the estimated counter minutes with the raw counts behind them, the gap to
 * the pharmacy's average hour, and the long acts when they weighed in.
 */
export const describeSlot = (slot: FrequentationHour, averageMinutes: number): {title: string; lines: string[]} => {
    const lines = [
        `${integer.format(Math.round(slot.minutes))} min de comptoir · ${plural(Math.round(slot.lines), 'ligne', 'lignes')} · ${plural(Math.round(slot.carts), 'passage', 'passages')}`
    ];
    const gap = deviation(slot.minutes, averageMinutes);

    if (gap) {
        lines.push(`${gap} par rapport à votre heure moyenne`);
    }

    const acts = describeActs(slot.acts);

    if (acts) {
        lines.push(`Dont ${acts}`);
    }

    return {
        title: `${slot.hour} h · ${capitalize(labelForLevel(slot.level))}`,
        lines
    };
};

/**
 * The busiest hours of a day, most loaded first — what a column header can
 * say about its day when the hours themselves cannot carry a tooltip.
 */
export const busiestHours = (day: FrequentationDay, count = 3): FrequentationHour[] => {
    return [...day.hours].sort((a, b) => b.minutes - a.minutes).slice(0, count);
};

/**
 * What a day header says about its day: the peak level and the hours to
 * avoid.
 */
export const describeDay = (day: FrequentationDay): {title: string; lines: string[]} => {
    // reduce, not Math.max(...[]): a day without hours must not read as a peak.
    const peak = day.hours.reduce((max, slot) => Math.max(max, slot.level), 1);
    const hours = busiestHours(day).sort((a, b) => a.hour - b.hour);

    return {
        title: `Pic : ${capitalize(labelForLevel(peak))}`,
        lines: hours.length
            ? [`Heures les plus chargées : ${hours.map((slot) => `${slot.hour} h`).join(', ')}`]
            : []
    };
};

/**
 * What justifies the colour of a week in the yearly view.
 */
export const describeYearWeek = (week: FrequentationYearWeek, medianMinutes: number): {title: string; lines: string[]} => {
    if (week.partial) {
        return {
            title: 'Semaine en cours',
            lines: [`${integer.format(week.minutes)} min de comptoir jusqu'ici`]
        };
    }

    const lines = [`${integer.format(week.minutes)} min de comptoir · ${plural(week.carts, 'passage', 'passages')}`];
    const gap = deviation(week.minutes, medianMinutes);

    if (gap) {
        lines.push(`${gap} par rapport à votre semaine médiane`);
    }

    const acts = describeActs(week.acts);

    if (acts) {
        lines.push(`Dont ${acts}`);
    }

    return {title: capitalize(labelForLevel(week.level)), lines};
};
